import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataExternalApi, ProxyConfigBasic } from '@onefootprint/request-types/dashboard';
import { Checkbox, CodeBlock, Form, Select, TextInput } from '@onefootprint/ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CollapsibleSection from '@/components/playbook-builder/components/collapsible-section';
import { type ExternalApiFormData, MOCK_RESPONSE_VALUE } from '@/components/playbook-builder/components/external-api-node/types';
import useOrgSession from '@/hooks/use-org-session';
import { getDefaultValues } from '../../utils/get-default-values';
import OnboardingVariables from '../onboarding-variables';
import Parameters from '../parameters';
import PathInput from '../path-input';
export type ConfigurationSectionProps = {
  node: {
    id: string;
    data: ApiNodeDataExternalApi;
  };
  isCollapsible?: boolean;
  proxyConfig: ProxyConfigBasic | undefined;
  sandboxProxyConfig: ProxyConfigBasic | undefined;
};
const ConfigurationSection = t0 => {
  const $ = _c(7);
  const {
    node,
    isCollapsible: t1,
    proxyConfig,
    sandboxProxyConfig
  } = t0;
  const isCollapsible = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "externalApi"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t2);
  let t3;
  if ($[1] !== isCollapsible || $[2] !== node || $[3] !== proxyConfig || $[4] !== sandboxProxyConfig || $[5] !== t) {
    t3 = isCollapsible ? <CollapsibleSection title={t(_temp)} className="p-4"><ConfigurationSectionContent node={node} proxyConfig={proxyConfig} sandboxProxyConfig={sandboxProxyConfig} /></CollapsibleSection> : <div className="border border-solid border-tertiary rounded-md"><div className="py-3 px-4 border-b border-solid border-tertiary"><h3 className="text-label-2 text-primary">{t(_temp2)}</h3></div><div className="py-4 px-4"><ConfigurationSectionContent node={node} proxyConfig={proxyConfig} sandboxProxyConfig={sandboxProxyConfig} /></div></div>;
    $[1] = isCollapsible;
    $[2] = node;
    $[3] = proxyConfig;
    $[4] = sandboxProxyConfig;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const ConfigurationSectionContent = t0 => {
  const $ = _c(85);
  const {
    node,
    proxyConfig,
    sandboxProxyConfig
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "externalApi"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const rawConfig = node.data.config as Record<string, unknown>;
  let t2;
  if ($[1] !== rawConfig.headers) {
    t2 = !!rawConfig.headers && Object.keys(rawConfig.headers as object).length > 0;
    $[1] = rawConfig.headers;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hasHeaders = t2;
  let t3;
  if ($[3] !== rawConfig.queryParams) {
    t3 = !!rawConfig.queryParams && Object.keys(rawConfig.queryParams as object).length > 0;
    $[3] = rawConfig.queryParams;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasQueryParams = t3;
  let t4;
  if ($[5] !== rawConfig.body) {
    t4 = !!rawConfig.body && Object.keys(rawConfig.body as object).length > 0;
    $[5] = rawConfig.body;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasBody = t4;
  let t5;
  if ($[7] !== rawConfig.workflowDataAssignments) {
    t5 = !!rawConfig.workflowDataAssignments && Object.keys(rawConfig.workflowDataAssignments as object).length > 0;
    $[7] = rawConfig.workflowDataAssignments;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const hasWorkflowDataAssignments = t5;
  const shouldShowBody = rawConfig.method !== "get";
  const sandboxHttp = rawConfig.sandboxHttpProperties as Record<string, unknown> | undefined;
  const hasSandboxHttp = !!sandboxHttp;
  const sandboxHeaders = sandboxHttp?.headers as Record<string, unknown> | undefined;
  let t6;
  if ($[9] !== sandboxHeaders) {
    t6 = sandboxHeaders && Object.keys(sandboxHeaders).length > 0;
    $[9] = sandboxHeaders;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const hasSandboxHeaders = t6;
  const sandboxQueryParams = sandboxHttp?.queryParams as Record<string, unknown> | undefined;
  let t7;
  if ($[11] !== sandboxQueryParams) {
    t7 = sandboxQueryParams && Object.keys(sandboxQueryParams).length > 0;
    $[11] = sandboxQueryParams;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const hasSandboxQueryParams = t7;
  const sandboxBody = sandboxHttp?.body as string | undefined;
  const hasSandboxBody = !!sandboxBody;
  const shouldShowSandboxBody = sandboxHttp?.method !== "get";
  const {
    sandbox
  } = useOrgSession();
  let t8;
  if ($[13] !== node || $[14] !== proxyConfig || $[15] !== sandbox.isSandbox || $[16] !== sandboxProxyConfig) {
    t8 = getDefaultValues({
      node,
      proxyConfig,
      sandboxProxyConfig,
      isSandboxSession: sandbox.isSandbox
    });
    $[13] = node;
    $[14] = proxyConfig;
    $[15] = sandbox.isSandbox;
    $[16] = sandboxProxyConfig;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const defaultValues = t8;
  let t9;
  if ($[18] !== defaultValues) {
    t9 = {
      defaultValues,
      disabled: true
    };
    $[18] = defaultValues;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const methods = useForm(t9);
  const t10 = node.data.name;
  let t11;
  if ($[20] !== t) {
    t11 = t(_temp3);
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t(_temp4);
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== node.data.name || $[25] !== t11 || $[26] !== t12) {
    t13 = <TextInput disabled={true} value={t10} label={t11} placeholder={t12} />;
    $[24] = node.data.name;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== proxyConfig || $[29] !== t) {
    t14 = proxyConfig && <Form.Select name="config.proxyConfig" label={t(_temp5)} disabled={true} options={[{
      label: proxyConfig.name,
      value: proxyConfig.id
    }]} />;
    $[28] = proxyConfig;
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== defaultValues.config.sandboxProxyConfig || $[32] !== defaultValues.config.useMockData || $[33] !== sandboxProxyConfig || $[34] !== t) {
    t15 = defaultValues.config.sandboxProxyConfig === MOCK_RESPONSE_VALUE ? <><TextInput disabled={true} value={t(_temp6)} label={t(_temp7)} />{defaultValues.config.useMockData && <CodeBlock language="json" hideHeader={true}>{defaultValues.config.useMockData}</CodeBlock>}</> : sandboxProxyConfig && <Form.Select name="config.sandboxProxyConfig" label={t(_temp8)} disabled={true} options={[{
      label: sandboxProxyConfig.name,
      value: sandboxProxyConfig.id
    }]} />;
    $[31] = defaultValues.config.sandboxProxyConfig;
    $[32] = defaultValues.config.useMockData;
    $[33] = sandboxProxyConfig;
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <PathInput autocompleteItems={{}} />;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== hasHeaders || $[38] !== t) {
    t17 = hasHeaders && <Parameters disabled={true} fieldName="config.headers" title={t(_temp9)} autocompleteItems={{}} />;
    $[37] = hasHeaders;
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== hasQueryParams || $[41] !== t) {
    t18 = hasQueryParams && <Parameters disabled={true} fieldName="config.queryParams" title={t(_temp10)} autocompleteItems={{}} />;
    $[40] = hasQueryParams;
    $[41] = t;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== defaultValues.config.body || $[44] !== hasBody || $[45] !== shouldShowBody) {
    t19 = hasBody && shouldShowBody && <CodeBlock hideHeader={true} language="json" showLineNumbers={true} backgroundColor="secondary">{defaultValues.config.body}</CodeBlock>;
    $[43] = defaultValues.config.body;
    $[44] = hasBody;
    $[45] = shouldShowBody;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== defaultValues.config.sandboxHttpProperties || $[48] !== hasSandboxBody || $[49] !== hasSandboxHeaders || $[50] !== hasSandboxHttp || $[51] !== hasSandboxQueryParams || $[52] !== shouldShowSandboxBody || $[53] !== t) {
    t20 = hasSandboxHttp && <><div className="text-label-2 text-primary">{t(_temp11)}</div><PathInput autocompleteItems={{}} fieldPrefix="config.sandboxHttpProperties." />{hasSandboxHeaders && <Parameters disabled={true} fieldName="config.sandboxHttpProperties.headers" title={t(_temp12)} autocompleteItems={{}} />}{hasSandboxQueryParams && <Parameters disabled={true} fieldName="config.sandboxHttpProperties.queryParams" title={t(_temp13)} autocompleteItems={{}} />}{hasSandboxBody && shouldShowSandboxBody && <CodeBlock hideHeader={true} language="json" showLineNumbers={true} backgroundColor="secondary">{defaultValues.config.sandboxHttpProperties.body}</CodeBlock>}</>;
    $[47] = defaultValues.config.sandboxHttpProperties;
    $[48] = hasSandboxBody;
    $[49] = hasSandboxHeaders;
    $[50] = hasSandboxHttp;
    $[51] = hasSandboxQueryParams;
    $[52] = shouldShowSandboxBody;
    $[53] = t;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== hasWorkflowDataAssignments) {
    t21 = hasWorkflowDataAssignments && <OnboardingVariables canEdit={false} />;
    $[55] = hasWorkflowDataAssignments;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t) {
    t22 = t(_temp14);
    $[57] = t;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t) {
    t23 = t(_temp15);
    $[59] = t;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== t23) {
    t24 = [{
      label: t23,
      value: "json"
    }];
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t22 || $[64] !== t24) {
    t25 = <Select label={t22} size="compact" value="json" options={t24} disabled={true} onChange={_temp16} />;
    $[63] = t22;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  const t26 = rawConfig.requireSuccessStatusCode as boolean ?? true;
  let t27;
  if ($[66] !== t) {
    t27 = t(_temp17);
    $[66] = t;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== t26 || $[69] !== t27) {
    t28 = <Checkbox disabled={true} checked={t26} label={t27} />;
    $[68] = t26;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t17 || $[75] !== t18 || $[76] !== t19 || $[77] !== t20 || $[78] !== t21 || $[79] !== t25 || $[80] !== t28) {
    t29 = <div className="flex flex-col gap-4 w-full">{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t25}{t28}</div>;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t17;
    $[75] = t18;
    $[76] = t19;
    $[77] = t20;
    $[78] = t21;
    $[79] = t25;
    $[80] = t28;
    $[81] = t29;
  } else {
    t29 = $[81];
  }
  let t30;
  if ($[82] !== methods || $[83] !== t29) {
    t30 = <FormProvider {...methods}>{t29}</FormProvider>;
    $[82] = methods;
    $[83] = t29;
    $[84] = t30;
  } else {
    t30 = $[84];
  }
  return t30;
};
export default ConfigurationSection;
function _temp($) {
  return $.configuration;
}
function _temp2($_0) {
  return $_0.configuration;
}
function _temp3($) {
  return $.nodeName.label;
}
function _temp4($_0) {
  return $_0.nodeName.placeholder;
}
function _temp5($_1) {
  return $_1.vaultProxyConfig.liveEnvironment;
}
function _temp6($_2) {
  return $_2.vaultProxyConfig.useMockResponse;
}
function _temp7($_3) {
  return $_3.vaultProxyConfig.sandboxEnvironment;
}
function _temp8($_4) {
  return $_4.vaultProxyConfig.sandboxEnvironment;
}
function _temp9($_5) {
  return $_5.requestConfig.headers;
}
function _temp10($_6) {
  return $_6.requestConfig.queryParameters;
}
function _temp11($_7) {
  return $_7.sandboxRequestConfig.title;
}
function _temp12($_8) {
  return $_8.requestConfig.headers;
}
function _temp13($_9) {
  return $_9.requestConfig.queryParameters;
}
function _temp14($_10) {
  return $_10.responseConfig.contentType.label;
}
function _temp15($_11) {
  return $_11.responseConfig.contentType.json;
}
function _temp16() {}
function _temp17($_12) {
  return $_12.responseConfig.statusCode.label;
}